import { useState, useEffect, useRef } from 'react'

/**
 * EditableCell — shared inline editing component used by Income, Projects, and Pitches.
 *
 * Props:
 *   value        — current value (string)
 *   onSave       — called with new value when user commits a change
 *   type         — input type: 'text' | 'number' | 'date' (default: 'text')
 *   options      — array of strings → renders a dropdown instead of a text input
 *   theme        — theme object from App.js
 *   isAmount     — formats value as $X,XXX and colors it accent
 *   isStatus     — renders a styled status <select> (pass statusOptions + statusColors too)
 *   statusOptions — array of status strings (required when isStatus=true)
 *   statusColors  — { [status]: { bg, text } } map (required when isStatus=true)
 *   followUpColor — override text color (used by Pitches for urgency coloring)
 */
export default function EditableCell({
  value,
  onSave,
  type = 'text',
  options = null,
  theme,
  isAmount = false,
  isStatus = false,
  statusOptions = [],
  statusColors = {},
  followUpColor = null,
  canEdit = true,
}) {
  const [editing, setEditing] = useState(false)
  const [val, setVal] = useState(value || '')
  const inputRef = useRef(null)

  useEffect(() => { setVal(value || '') }, [value])
  useEffect(() => { if (editing && inputRef.current) inputRef.current.focus() }, [editing])

  const handleSave = () => {
    setEditing(false)
    if (val !== value) onSave(val)
  }

  const inputStyle = {
    background: theme.surfaceAlt,
    border: `1px solid ${theme.accent}`,
    color: theme.text,
    padding: '4px 8px',
    fontSize: 13,
    fontFamily: "'Nunito Sans', sans-serif",
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box',
  }

  const displayStyle = {
    fontSize: 13,
    color: followUpColor || (isAmount ? theme.accent : theme.text),
    fontFamily: "'Nunito Sans', sans-serif",
    fontWeight: isAmount ? 700 : (val ? 600 : 400),
    cursor: canEdit ? 'pointer' : 'default',
    padding: '2px 4px',
    borderRadius: 2,
    minHeight: 22,
  }

  const hoverOn = e => { if (canEdit) e.currentTarget.style.background = theme.surfaceAlt }
  const hoverOff = e => { e.currentTarget.style.background = 'transparent' }

  // Status — badge when read-only, dropdown when editable
  if (isStatus) {
    return (
      <div style={{
        display: 'inline-block',
        background: statusColors[val]?.bg,
        color: statusColors[val]?.text,
        padding: '4px 8px',
        fontSize: 11,
        fontFamily: "'Open Sans', sans-serif",
        letterSpacing: '0.05em',
        width: '100%',
      }}>
        {canEdit ? (
          <select
            value={val}
            onChange={e => { setVal(e.target.value); onSave(e.target.value) }}
            style={{
              background: 'transparent', color: 'inherit', border: 'none',
              padding: 0, fontSize: 11, fontFamily: 'inherit',
              letterSpacing: '0.05em', cursor: 'pointer', outline: 'none', width: '100%',
            }}
          >
            {statusOptions.map(s => <option key={s}>{s}</option>)}
          </select>
        ) : val}
      </div>
    )
  }

  // Options dropdown — click to open, blur to save
  if (options) {
    return editing ? (
      <select
        ref={inputRef}
        value={val}
        onChange={e => setVal(e.target.value)}
        onBlur={handleSave}
        style={inputStyle}
      >
        {options.map(o => <option key={o}>{o}</option>)}
      </select>
    ) : (
      <div
        onClick={() => { if (canEdit) setEditing(true) }}
        style={{ ...displayStyle, color: followUpColor || theme.textSecondary }}
        onMouseEnter={hoverOn}
        onMouseLeave={hoverOff}
        title={canEdit ? 'Click to edit' : undefined}
      >
        {val || '—'}
      </div>
    )
  }

  // Default: text / number / date input
  return editing ? (
    <input
      ref={inputRef}
      type={type}
      value={val}
      onChange={e => setVal(e.target.value)}
      onBlur={handleSave}
      onKeyDown={e => e.key === 'Enter' && handleSave()}
      style={inputStyle}
    />
  ) : (
    <div
      onClick={() => { if (canEdit) setEditing(true) }}
      style={displayStyle}
      onMouseEnter={hoverOn}
      onMouseLeave={hoverOff}
      title={canEdit ? 'Click to edit' : undefined}
    >
      {isAmount
        ? (val ? `$${Number(val).toLocaleString()}` : '—')
        : type === 'date' && val
          ? new Date(val + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
          : (val || '—')}
    </div>
  )
}